import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import {
  ApiErrorParser,
  AuthorizationException,
  CoreException,
  Failure,
  InvalidOrganizationException,
  InvalidTokenException,
  LimitExceededException,
  Response,
  Success,
  isTrustedRedirectUrl
} from '@sdk/sdk';
import { firstValueFrom } from 'rxjs';
import {
  AssistantAuthorization,
  AssistantDevicesRepository,
  CliAuthorizationRequest,
  Organization
} from '../auth';
import { ApiAssistantAuthorizationMap } from './api-assistant-authorization.map';
import { ApiAssistantAuthorizationMapper } from './api-assistant-authorization.mapper';
import { ApiSessionService } from './api-session.service';
import { environment } from 'src/environments/environment';

const AUTHORIZE_PATH = 'api/assistant/devices/authorize/';
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_TOO_MANY_REQUESTS = 429;

@Injectable({
  providedIn: 'root'
})
export class ApiAssistantDevicesRepository implements AssistantDevicesRepository {
  private _errorParser = new ApiErrorParser();
  private _sessionService = new ApiSessionService();

  constructor(private _http: HttpClient) {}

  async authorize(
    organization: Organization,
    request: CliAuthorizationRequest
  ): Promise<Response<AssistantAuthorization, CoreException>> {
    // The session token goes to the organization's own host, so that host is
    // pinned here instead of trusting the CSP and the organization list alone.
    const isTrustedInstance = this._isTrustedInstance(organization.url);
    if (!isTrustedInstance) {
      const exception = new InvalidOrganizationException();
      return new Failure(exception);
    }

    const url = new URL(AUTHORIZE_PATH, organization.url);
    const body = this._authorizeBody(organization, request);
    let response: ApiAssistantAuthorizationMap;
    try {
      const token = await this._sessionService.getToken();
      const options = { headers: { Authorization: `Bearer ${token}`, Organization: organization.id } };
      const post = this._http.post<ApiAssistantAuthorizationMap>(url.toString(), body, options);
      response = await firstValueFrom(post);
    } catch (error) {
      const exception = this._toException(error);
      return new Failure(exception);
    }

    const mapper = new ApiAssistantAuthorizationMapper(organization.url.host);
    const authorization = mapper.fromMap(response);
    return new Success(authorization);
  }

  /**
   * An https `*.sytex.io` host, or the local dummy organization's instance,
   * which only exists in development builds (same `isDevMode()` guard).
   */
  private _isTrustedInstance(instanceUrl: URL): boolean {
    const isHttps = instanceUrl.protocol === 'https:';
    const isSytexHost = isTrustedRedirectUrl(instanceUrl.href);
    const isSytexInstance = isHttps && isSytexHost;
    if (isSytexInstance || !isDevMode()) {
      return isSytexInstance;
    }
    const localDevUrl = new URL(environment.localDevUrl);
    return instanceUrl.href === localDevUrl.href;
  }

  /**
   * An MCP connection is bound to the organization: the instance checks the
   * user may work there and keeps it with the code. A CLI one is not, and the
   * CLI picks the organization each time it asks for a token.
   */
  private _authorizeBody(organization: Organization, request: CliAuthorizationRequest): object {
    if (request.isMcp) {
      return {
        client_name: request.name,
        code_challenge: request.codeChallenge,
        organization_id: Number(organization.id)
      };
    }
    return { name: request.name, code_challenge: request.codeChallenge };
  }

  private _toException(error: unknown): CoreException {
    if (error instanceof InvalidTokenException) {
      return error;
    }
    const isHttpError = error instanceof HttpErrorResponse;
    // 401/403 from the instance: no license, or a login method the
    // organization does not accept. Not a session problem, so not a retry.
    const isRefused = isHttpError && (error.status === HTTP_UNAUTHORIZED || error.status === HTTP_FORBIDDEN);
    if (isRefused) {
      return new AuthorizationException();
    }
    const isRateLimited = isHttpError && error.status === HTTP_TOO_MANY_REQUESTS;
    if (isRateLimited) {
      return new LimitExceededException();
    }
    return this._errorParser.parse(error);
  }
}
